import React from 'react';
import LegalLayout from '../legal/LegalLayout';
import {
  PrivacyShieldIllustration,
  ThirdPartyIllustration,
  FeesIllustration,
} from '../legal/illustrations';
import { openCookieSettings } from '../legal/cookieConsent';

/**
 * Datenschutzerklärung for the MainEvents platform (operated by Seuytel).
 * Company data is filled in; items in [eckigen Klammern] still need real values
 * (legal form, managing director, register/VAT, data protection officer).
 */
export default function Datenschutzerklaerung() {
  return (
    <LegalLayout
      eyebrow="Datenschutz"
      title="Datenschutzerklärung"
      intro="Der Schutz Ihrer Daten ist uns wichtig. Hier erfahren Sie, welche Daten wir bei der Nutzung von MainEvents verarbeiten, zu welchem Zweck und welche Rechte Ihnen zustehen."
      art={<PrivacyShieldIllustration />}
    >
      <h2>1. Verantwortlicher</h2>
      <p>
        Verantwortlich für die Datenverarbeitung im Sinne der
        Datenschutz-Grundverordnung (DSGVO) ist:
      </p>
      <div className="se-card">
        Seuytel UG (haftungsbeschränkt)
        <br />
        Walkürenring 4<br />
        38106 Braunschweig<br />
        Deutschland
        <br />
        <br />
        E-Mail: <a href="mailto:support@mainevents.seuytel.com">support@mainevents.seuytel.com</a>
        <br />
        Vertreten durch: <span className="se-placeholder">Rayan Dofang Seuyep / Geschäftführer</span>
      </div>
      <p>
        Einen Datenschutzbeauftragten haben wir{' '}
        <span className="se-placeholder">[benannt: Name &amp; Kontakt / nicht benannt, da gesetzlich nicht erforderlich]</span>.
      </p>

      <h2>2. Geltungsbereich</h2>
      <p>
        Diese Erklärung gilt für die MainEvents-Plattform – die mobile App
        (Android/iOS) sowie die Webangebote unter{' '}
        <code>mainevents.seuytel.com</code> und den zugehörigen Seiten zur
        Saalplan-/Sitzplatzauswahl. Über diese Angebote können Sie Veranstaltungen
        entdecken, Tickets kaufen und verkaufen, sich mit anderen austauschen und
        Events organisieren.
      </p>

      <h2>3. Welche Daten wir verarbeiten</h2>
      <ul>
        <li>
          <strong>Konto- und Profildaten:</strong> Name, E-Mail-Adresse,
          Passwort (verschlüsselt), Profilbild, Konto-Typ, ggf. Unternehmensdaten.
        </li>
        <li>
          <strong>Bestell- und Ticketdaten:</strong> gekaufte/verkaufte Tickets,
          Sitzplätze, Bestellverlauf, Rückerstattungen, Weiterverkäufe.
        </li>
        <li>
          <strong>Zahlungsdaten:</strong> abgewickelt über Stripe und PayPal –
          Kartendaten werden ausschließlich bei den Zahlungsdienstleistern
          verarbeitet, nicht bei uns gespeichert.
        </li>
        <li>
          <strong>Kommunikationsdaten:</strong> Chat-Nachrichten, Medien,
          Sprachnachrichten, Gruppen- und Kanalinhalte.
        </li>
        <li>
          <strong>Standort- und Kartendaten:</strong> bei Nutzung der Karten-,
          Veranstaltungsort- und Sitzplanfunktionen (Google Maps/Places).
        </li>
        <li>
          <strong>Geräte- und Nutzungsdaten:</strong> Geräte-Kennungen,
          Push-Token, IP-Adresse, Log- und Zugriffsdaten.
        </li>
      </ul>

      <h2>4. Zwecke und Rechtsgrundlagen</h2>
      <ul>
        <li>
          Vertragserfüllung – Bereitstellung der Plattform, Ticketkauf/-verkauf,
          Kommunikation (Art. 6 Abs. 1 lit. b DSGVO).
        </li>
        <li>
          Rechtliche Verpflichtungen – z. B. steuer- und handelsrechtliche
          Aufbewahrung (Art. 6 Abs. 1 lit. c DSGVO).
        </li>
        <li>
          Berechtigte Interessen – Sicherheit, Betrugsprävention, Verbesserung
          der Plattform (Art. 6 Abs. 1 lit. f DSGVO).
        </li>
        <li>
          Einwilligung – nicht-essenzielle Cookies, Standort, Marketing
          (Art. 6 Abs. 1 lit. a DSGVO). Widerruf jederzeit möglich.
        </li>
      </ul>

      <h2>5. Cookies und Einwilligungsverwaltung</h2>
      <p>
        Wir verwenden Cookies und ähnliche Technologien. Essenzielle Cookies sind
        für den Betrieb erforderlich; alle übrigen setzen wir nur mit Ihrer
        Einwilligung. Einzelheiten finden Sie in unserer{' '}
        <a href="/cookies">Cookie-Richtlinie</a>. Ihre Auswahl können Sie jederzeit
        anpassen:
      </p>
      <button type="button" className="se-reopen" onClick={openCookieSettings}>
        Cookie-Einstellungen öffnen
      </button>

      <div className="se-section-head">
        <span className="se-mini-art">
          <ThirdPartyIllustration />
        </span>
        <h2 style={{ margin: 0 }}>6. Drittanbieter und Empfänger</h2>
      </div>
      <p>
        Zur Bereitstellung einzelner Funktionen binden wir externe Dienste ein.
        Dabei können personenbezogene Daten an die jeweiligen Anbieter übermittelt
        werden. Maßgeblich sind auch deren Datenschutzbestimmungen.
      </p>
      <div className="se-table-wrap">
        <table className="se-table">
          <thead>
            <tr>
              <th>Dienst</th>
              <th>Zweck</th>
              <th>Anbieter</th>
              <th>Datenschutz</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Stripe</td>
              <td>Zahlungsabwicklung, Auszahlung, Betrugsprävention</td>
              <td>Stripe Payments Europe, Ltd. (IE) / Stripe, Inc. (US)</td>
              <td>
                <a href="https://stripe.com/de/privacy" target="_blank" rel="noreferrer">
                  stripe.com/privacy
                </a>
              </td>
            </tr>
            <tr>
              <td>PayPal</td>
              <td>Zahlungsabwicklung, Auszahlung</td>
              <td>PayPal (Europe) S.à r.l. et Cie, S.C.A. (LU)</td>
              <td>
                <a href="https://www.paypal.com/de/legalhub/privacy-full" target="_blank" rel="noreferrer">
                  paypal.com/privacy
                </a>
              </td>
            </tr>
            <tr>
              <td>Google Maps / Places</td>
              <td>Karten, Veranstaltungsorte, Standortsuche, Sitzplan</td>
              <td>Google Ireland Ltd. (IE) / Google LLC (US)</td>
              <td>
                <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer">
                  policies.google.com/privacy
                </a>
              </td>
            </tr>
            <tr>
              <td>Firebase Cloud Messaging</td>
              <td>Push-Benachrichtigungen</td>
              <td>Google Ireland Ltd. (IE) / Google LLC (US)</td>
              <td>
                <a href="https://firebase.google.com/support/privacy" target="_blank" rel="noreferrer">
                  firebase.google.com/privacy
                </a>
              </td>
            </tr>
            <tr>
              <td>Pusher</td>
              <td>Echtzeit-Benachrichtigungen / Chat</td>
              <td>Pusher Ltd. (UK)</td>
              <td>
                <a href="https://pusher.com/legal/privacy-policy" target="_blank" rel="noreferrer">
                  pusher.com/privacy
                </a>
              </td>
            </tr>
            <tr>
              <td>App-Stores &amp; Push</td>
              <td>App-Auslieferung, Benachrichtigungen</td>
              <td>Apple Inc. (US) / Google LLC (US)</td>
              <td>
                <span className="se-placeholder">[Links ergänzen]</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <p className="se-callout">
        Bitte prüfen und ergänzen Sie diese Liste anhand der tatsächlich
        eingesetzten Dienste (z. B. Hosting-Anbieter, E-Mail-Versand, Analyse) und
        halten Sie die Anbieterangaben aktuell.
      </p>

      <h2>7. Übermittlung in Drittländer</h2>
      <p>
        Einige der genannten Anbieter verarbeiten Daten auch außerhalb der EU/des
        EWR, insbesondere in den USA. Soweit ein Angemessenheitsbeschluss (z. B.
        EU-US Data Privacy Framework) nicht greift, erfolgt die Übermittlung auf
        Grundlage von EU-Standardvertragsklauseln (Art. 46 DSGVO) oder Ihrer
        ausdrücklichen Einwilligung (Art. 49 Abs. 1 lit. a DSGVO).
      </p>

      <h2>8. Speicherdauer</h2>
      <p>
        Wir speichern personenbezogene Daten nur so lange, wie es für die
        genannten Zwecke erforderlich ist oder gesetzliche Aufbewahrungsfristen
        (z. B. handels- und steuerrechtlich bis zu 10 Jahre) dies vorschreiben.
        Anschließend werden die Daten gelöscht oder anonymisiert.
      </p>

      <h2>9. Ihre Rechte</h2>
      <p>Ihnen stehen nach der DSGVO insbesondere folgende Rechte zu:</p>
      <ul>
        <li>Auskunft über Ihre gespeicherten Daten (Art. 15)</li>
        <li>Berichtigung unrichtiger Daten (Art. 16)</li>
        <li>Löschung (Art. 17) und Einschränkung der Verarbeitung (Art. 18)</li>
        <li>Datenübertragbarkeit (Art. 20)</li>
        <li>Widerspruch gegen die Verarbeitung (Art. 21)</li>
        <li>Widerruf erteilter Einwilligungen mit Wirkung für die Zukunft (Art. 7 Abs. 3)</li>
      </ul>
      <p>
        Zur Ausübung Ihrer Rechte genügt eine Nachricht an{' '}
        <a href="mailto:support@mainevents.seuytel.com">support@mainevents.seuytel.com</a>.
      </p>
      <p>
        <strong>Selbstbedienung:</strong> Ihr Recht auf Löschung (Art. 17 DSGVO) können Sie auch direkt ausüben –
        löschen Sie Ihr Konto über die Seite <a href="/profil-loeschen">Profil löschen</a>. Bei der Löschung wird Ihr
        Profil deaktiviert und ist nicht mehr in der Suche auffindbar. Eine Löschung ist nicht möglich, solange Sie
        noch <strong>aktive Veranstaltungen mit zahlenden Teilnehmern</strong> haben; bitte schließen oder stornieren
        Sie diese zuvor (inkl. erforderlicher Rückerstattungen). Daten, die gesetzlichen Aufbewahrungspflichten
        unterliegen, werden bis zum Ablauf der jeweiligen Frist gesperrt und anschließend gelöscht.
      </p>
      <p>
        <strong>Inhalte melden:</strong> Verstößt ein Nutzerprofil oder ein Event gegen unsere Richtlinien oder gegen
        geltendes Recht, können Sie dies über die Seite <a href="/profil-melden">Profil oder Event melden</a> anzeigen.
        Wir verarbeiten die dabei angegebenen Daten (gemeldetes Objekt, Grund, Beschreibung) zur Prüfung und Bearbeitung
        der Meldung auf Grundlage unseres berechtigten Interesses an einer sicheren Plattform (Art. 6 Abs. 1 lit. f
        DSGVO).
      </p>

      <h2>10. Beschwerderecht</h2>
      <p>
        Sie haben das Recht, sich bei einer Datenschutz-Aufsichtsbehörde zu
        beschweren. Für uns zuständig ist{' '}
        <span className="se-placeholder">
          [Die Landesbeauftragte für den Datenschutz Niedersachsen]
        </span>
        .
      </p>

      <div className="se-section-head">
        <span className="se-mini-art">
          <FeesIllustration />
        </span>
        <h2 style={{ margin: 0 }}>11. Plattformgebühren &amp; Zahlungen</h2>
      </div>
      <p>
        MainEvents ist eine Vermittlungsplattform: Veranstalter bieten Tickets an,
        die Zahlungsabwicklung erfolgt über Stripe und PayPal. Für die Nutzung der
        Plattform können Service-/Plattformgebühren anfallen, die vor Abschluss des
        Kaufs transparent im Bestellüberblick ausgewiesen werden. Im Rahmen der
        Zahlungsabwicklung werden die hierfür notwendigen Daten an die
        Zahlungsdienstleister und – bei Auszahlungen an Veranstalter – an die
        jeweiligen Konten übermittelt.
      </p>
      <p className="se-callout">
        Die genaue Höhe und Aufschlüsselung der Gebühren bitte gemäß Ihrem
        Preismodell ergänzen (siehe auch AGB).
      </p>

      <h2>12. Änderungen dieser Erklärung</h2>
      <p>
        Wir passen diese Datenschutzerklärung an, wenn sich die Rechtslage oder
        unsere Verarbeitung ändert. Es gilt die jeweils hier veröffentlichte
        aktuelle Fassung.
      </p>

      <p style={{ color: 'var(--se-muted)', fontSize: 13 }}>
        Stand:{' '}
        {new Date().toLocaleDateString('de-DE', {
          year: 'numeric',
          month: 'long',
        })}
      </p>
    </LegalLayout>
  );
}
